<script setup>
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  isPhoneE164OrEmpty,
  hasNoSpacesOrSpecialCharacters,
} from 'shared/helpers/Validators';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const DEFAULT_WEBHOOK_EVENTS = ['connection', 'messages', 'messages_update'];

const hasWebhookSelection = helpers.withMessage(
  () => t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.ERROR'),
  value => Array.isArray(value) && value.length > 0
);

const state = reactive({
  inboxName: '',
  phoneNumber: '',
  instanceToken: '',
  instanceId: '',
  webhookEvents: [...DEFAULT_WEBHOOK_EVENTS],
});

const rules = {
  inboxName: {
    required: helpers.withMessage(
      () => t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR'),
      required
    ),
    format: helpers.withMessage(
      () => t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.FORMAT_ERROR'),
      value => hasNoSpacesOrSpecialCharacters(value)
    ),
  },
  phoneNumber: { required, isPhoneE164OrEmpty },
  instanceToken: {
    required: helpers.withMessage(
      () => t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_TOKEN.ERROR'),
      required
    ),
  },
  webhookEvents: { hasWebhookSelection },
};

const v$ = useVuelidate(rules, state);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const inboxNameError = computed(
  () => v$.value.inboxName.$errors[0]?.$message || ''
);

const instanceTokenError = computed(
  () => v$.value.instanceToken.$errors[0]?.$message || ''
);

const webhookEventsError = computed(
  () => v$.value.webhookEvents.$errors[0]?.$message || ''
);

const webhookEventOptions = computed(() => [
  {
    value: 'connection',
    label: t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.CONNECTION'),
  },
  {
    value: 'messages',
    label: t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.MESSAGES'),
  },
  {
    value: 'messages_update',
    label: t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.MESSAGES_UPDATE'),
  },
]);

const providerConfig = () => {
  const config = {
    instance_token: state.instanceToken,
    instance_name: state.inboxName?.trim(),
    webhook_events: state.webhookEvents,
  };

  if (state.instanceId) {
    config.instance_id = state.instanceId;
  }

  return config;
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: state.inboxName?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: state.phoneNumber,
        provider: 'uazapi',
        provider_config: providerConfig(),
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="state.inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ inboxNameError }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="state.phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.instanceToken.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_TOKEN.LABEL') }}
        <input
          v-model="state.instanceToken"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_TOKEN.PLACEHOLDER')"
          @blur="v$.instanceToken.$touch"
        />
        <span v-if="v$.instanceToken.$error" class="message">
          {{ instanceTokenError }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_ID.LABEL') }}
        <input
          v-model="state.instanceId"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_ID.PLACEHOLDER')"
        />
        <span class="message info">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INSTANCE_ID.HELPER') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <fieldset :class="{ error: v$.webhookEvents.$error }">
        <legend class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.LABEL') }}
        </legend>
        <p class="mb-3 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WEBHOOK_EVENTS.HELPER') }}
        </p>
        <div class="flex flex-col gap-2">
          <label
            v-for="option in webhookEventOptions"
            :key="option.value"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="state.webhookEvents"
              type="checkbox"
              :value="option.value"
              @change="v$.webhookEvents.$touch"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>
        <span v-if="v$.webhookEvents.$error" class="message">
          {{ webhookEventsError }}
        </span>
      </fieldset>
    </div>

    <div class="w-full">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        :is-loading="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
